import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { FiCheck } from "react-icons/fi";
import {
  Container,
  CircleButton,
  Popover,
  PopoverTitle,
  PopoverItem,
  PopoverEmpty,
} from "./styles";

/**
 * Checkbox redondo para sinalizar status de uma sub-etapa (Terceiros,
 * Acabamento etc.).
 *
 *  - `status.aplicavel === false`  → renderiza espacador invisivel (n-a)
 *  - `status.aplicavel && completo` → verde com check
 *  - `status.aplicavel && !completo` → vazio (apenas borda)
 *
 * Click abre popover com a lista de itens (recebidos / pendentes).
 */
export default function StatusCheckbox({ status, title, tag = "" }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onClickFora(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    function onEsc(event) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClickFora);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickFora);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!status || !status.aplicavel) {
    // Espacador invisivel — preserva alinhamento entre linhas com e sem
    // checkboxes (consistencia visual).
    return <Container aria-hidden="true" $placeholder />;
  }

  const completo = Boolean(status.completo);

  return (
    <Container ref={containerRef}>
      <CircleButton
        type="button"
        $completo={completo}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        title={title}
        aria-label={`${title} — ${completo ? "concluído" : "pendente"}`}
      >
        {completo ? <FiCheck size={11} strokeWidth={3} /> : tag}
      </CircleButton>

      <AnimatePresence>
        {open && (
          <Popover
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
          >
            <PopoverTitle>{title}</PopoverTitle>
            {(status.itens || []).length === 0 ? (
              <PopoverEmpty>Sem itens registrados.</PopoverEmpty>
            ) : (
              (status.itens || []).map((item, idx) => (
                <PopoverItem key={`${item.label}-${idx}`}>
                  <strong>{item.label}</strong>
                  {item.descricao && <span>{item.descricao}</span>}
                </PopoverItem>
              ))
            )}
          </Popover>
        )}
      </AnimatePresence>
    </Container>
  );
}
